import {useRef, useState} from "react";

// The import-match state machine shared by every dataset's contact form: it owns the dialog, the
// preview toggle and the find_match request. Each form keeps the one dataset-shaped part - how a
// matched payload is written onto its own state.
const EMPTY = {match: null, name: '', open: false, records: []};

// Family Office renames the person's name columns after its own contact, so both spellings are
// read here rather than making every FO form pass a nameFor of its own.
const defaultNameFor = (match) => ([
    match?.contact_first_name || match?.first_name,
    match?.contact_last_name || match?.last_name,
].filter(Boolean).join(' '));

export function usePersonMatch(
    {
        findMatch,
        baseParams,
        nameFor = defaultNameFor,
        canImport = () => true,
        onAccept,
        onDecline,
        dismissible = false,
        // Set false where the form still has to read the matched person after a refusal.
        clearOnDecline = true,
    }
) {
    const [matchData, setMatchData] = useState(EMPTY);
    const [previewOpen, setPreviewOpen] = useState(false);
    const lastRequestId = useRef(0);
    // The match's per-dataset records ride next to it in the response, never inside it: the forms
    // spread the match onto their own state.
    const lastRecords = useRef([]);

    // Resolves to the endpoint's body: what counts as a match, as the contact already open, or
    // as taken differs per dataset. A lookup the form has already moved past never resolves, so a
    // slow answer to an earlier blur cannot open the dialog for - link, or undo - the wrong person.
    const request = (params) => {
        const base = typeof baseParams === 'function' ? baseParams() : (baseParams || {});
        const requestId = ++lastRequestId.current;

        return findMatch({...base, ...params})
            .then(({data}) => {
                if (requestId !== lastRequestId.current) return new Promise(() => {});

                lastRecords.current = data?.records || [];
                return data;
            });
    };

    // For a findable field cleared without a new lookup: the one still in flight is for a value
    // the form no longer holds.
    const cancel = () => {
        lastRequestId.current += 1;
    };

    const show = (match, {name, open = true} = {}) => setMatchData({
        match,
        name: name === undefined ? nameFor(match) : name,
        open,
        records: lastRecords.current,
    });

    // Keeps a match the form needs to read without prompting for it.
    const setSilently = (match) => setMatchData((prevState) => ({...prevState, match, records: lastRecords.current}));

    const close = () => setMatchData((prevState) => ({...prevState, open: false}));

    const reset = () => {
        setMatchData(EMPTY);
        setPreviewOpen(false);
    };

    const accept = () => {
        close();
        onAccept?.(matchData.match);
    };

    const decline = () => {
        if (clearOnDecline) {
            reset();
        } else {
            close();
        }
        onDecline?.();
    };

    return {
        match: matchData.match,
        name: matchData.name,
        previewOpen,
        request,
        cancel,
        show,
        setSilently,
        close,
        reset,
        openPreview: () => setPreviewOpen(true),
        closePreview: () => setPreviewOpen(false),
        dialogProps: {
            open: matchData.open,
            name: matchData.name,
            datasets: matchData.match?.datasets,
            canImport: matchData.match ? canImport(matchData.match) : true,
            onAccept: accept,
            onDecline: decline,
            onPreview: () => setPreviewOpen(true),
            dismissible,
        },
        previewProps: {
            open: previewOpen,
            onClose: () => setPreviewOpen(false),
            match: matchData.match,
            name: matchData.name,
            records: matchData.records,
            canImport: matchData.match ? canImport(matchData.match) : true,
            // Offered only while the match dialog is still waiting for an answer.
            onAccept: matchData.open ? () => {
                setPreviewOpen(false);
                accept();
            } : null,
        },
    };
}

export default usePersonMatch;
